import { protectedResourceAction } from '~/shell/utils/authUtil.server';
import { getLogger } from '../../../../logging/logger';
import { useTranslation } from 'react-i18next';

const log = getLogger('add-article-to-template-route');
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/common/components/ui/dialog';
import { Button } from '~/common/components/ui/button';
import { parseWithZod } from '@conform-to/zod/v4';
import { useEffect, useMemo, useState } from 'react';
import { addArticlesToTemplate, createTemplate } from '~/features/templates/templates.server';
import { createTemplateSchema } from '~/features/templates/schemas/createTemplateSchema';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/common/components/ui/tabs';
import { requireFormString } from '~/common/utils/formDataUtil';
import { cn } from '~/common/utils/styleUtil';
import { TemplateSelect } from '~/features/templates/components/TemplateSelect';
import { TemplateCreate } from '~/features/templates/components/TemplateCreate';
import { useAllTemplates } from '~/features/templates/routes/resources/AllTemplatesRoute';
import { LucideFilePlus } from 'lucide-react';
import { Media } from '~/media.ts';
import { setLatestTemplateIdOnContext } from '~/shell/globalContext.ts';
import { TgArea } from '~/common/types/area';
import { WebTemplateTypes } from '../../types/webTemplateType';

export type AddArticleToTemplateInput = {
  mainArticleId: number;
  isExternalArticle: boolean;
  quantity: number;
  // Optionale Tracking-Metadaten für Google Analytics (werden nicht an den Server gesendet).
  articleNumber?: string;
  articleDescriptionText?: string;
};

export const action = protectedResourceAction(async ({ request, area, t, api }) => {
  const formData = await request.formData();
  const articlesJson = requireFormString(formData, 'articles');
  const intent = formData.get(FORM_FIELDS.Intent);

  const articles: {
    mainArticleId: number;
    isExternalArticle: boolean;
    quantity: number;
  }[] = JSON.parse(articlesJson);

  if (intent === FORM_FIELDS.Create) {
    const submission = parseWithZod(formData, { schema: createTemplateSchema });

    if (submission.status !== 'success') {
      return submission.reply();
    }

    try {
      const templateType = area === TgArea ? WebTemplateTypes.BGH : WebTemplateTypes.AGH;
      const id = await createTemplate(api, submission.value.name, templateType);

      await addArticlesToTemplate(api, id, articles);

      //const headers = await updateSessionWithLatestTemplateId(request, id);
      //await updateSessionWithLatestTemplateId(request, id);
      setLatestTemplateIdOnContext(id);

      return Response.json(
        {
          ok: true,
          message: t('Bestellvorlage "{{template}}" erstellt und Artikel hinzugefügt', {
            template: submission.value.name
          })
        }
        //{ headers }
      );
    } catch (e) {
      log.error('Error adding article to template', e);
      return Response.json({ ok: false, message: t('Hinzufügen des Artikels fehlgeschlagen') });
    }
  }

  if (intent === FORM_FIELDS.Select) {
    const selectedTemplate = requireFormString(formData, 'selectedTemplateId');
    const selectedTemplateId = Number.parseInt(selectedTemplate);

    try {
      await addArticlesToTemplate(api, selectedTemplateId, articles);

      //const headers = await updateSessionWithLatestTemplateId(request, selectedTemplateId);
      //await updateSessionWithLatestTemplateId(request, selectedTemplateId);
      setLatestTemplateIdOnContext(selectedTemplateId);

      return Response.json(
        { ok: true, message: t('Artikel hinzugefügt') }
        //{ headers }
      );
    } catch (e) {
      log.error('Error adding article to template', e);
      return Response.json({ ok: false, message: t('Hinzufügen des Artikels fehlgeschlagen') });
    }
  }

  throw new Error('unsupported intent');
});

function getDefaultAddToTemplateText(isAgh: boolean, singularText: boolean | undefined): string {
  if (isAgh) {
    return singularText ? 'Zur Einkaufsliste hinzufügen' : 'Alle zur Einkaufsliste hinzufügen';
  }
  return singularText ? 'Zur Bestellvorlage hinzufügen' : 'Alle zur Bestellvorlage hinzufügen';
}

export function AddArticleToTemplateButton({
  articles,
  isAgh,
  className,
  disabled,
  onClose,
  variant = 'link',
  buttonText,
  singularText = true,
  showIcon = false,
  forceSize
}: Readonly<{
  className?: string;
  articles: AddArticleToTemplateInput[];
  isAgh: boolean;
  disabled?: boolean;
  onClose?: () => void;
  variant?: 'ghost' | 'default' | 'secondary' | 'primaryOutline' | 'link';
  buttonText?: string;
  singularText?: boolean;
  showIcon?: boolean;
  showLargeOnDesktop?: boolean;
  forceSize?: 'small' | 'large';
}>) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open && onClose) {
      onClose();
    }
  };

  const smallButton = useMemo(() => {
    return (
      <Button
        variant={'primaryOutline'}
        disabled={disabled}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
        }}
        className={cn('h-9 w-9 px-0', className)}
        data-testid={`addtotemplate-button`}
      >
        <LucideFilePlus size={18} />
      </Button>
    );
  }, [className, disabled]);

  const largeButton = useMemo(() => {
    return (
      <Button
        variant={variant}
        className={cn('w-full desktop:w-auto justify-center desktop:justify-start flex', className)}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
        }}
        data-testid={`addtotemplate-button`}
        disabled={disabled}
      >
        {showIcon && <LucideFilePlus size={18} className={'mr-2'} />}
        <span>{t((buttonText || getDefaultAddToTemplateText(isAgh, singularText)) as any)}</span>
      </Button>
    );
  }, [buttonText, className, disabled, isAgh, showIcon, singularText, t, variant]);

  return (
    <>
      <Media lessThan={'desktop'}>{forceSize === 'large' ? largeButton : smallButton}</Media>
      <Media greaterThanOrEqual={'desktop'}>{forceSize === 'small' ? smallButton : largeButton}</Media>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent showFullscreenOnMobile data-testid={'add-article-template-dialog'}>
          <DialogHeader>
            <DialogTitle>{t(isAgh ? 'Einkaufslisten' : 'Bestellvorlagen')}</DialogTitle>
            <DialogDescription hidden={true}>{t(isAgh ? 'Einkaufslisten' : 'Bestellvorlagen')}</DialogDescription>
          </DialogHeader>
          <TemplateTabs articles={articles} onClose={() => handleOpenChange(false)} isAgh={isAgh} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function TemplateTabs({
  onClose,
  articles,
  isAgh
}: Readonly<{
  onClose: () => void;
  articles: AddArticleToTemplateInput[];
  isAgh: boolean;
}>) {
  const { t } = useTranslation();
  const { templates, isLoading, latestTemplateId } = useAllTemplates(isAgh, articles);
  const [selectedTab, setSelectedTab] = useState(FORM_FIELDS.Select);

  useEffect(() => {
    if (templates?.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedTab(FORM_FIELDS.Create);
    }
  }, [templates]);

  return (
    <Tabs value={selectedTab} className={'flex flex-col'}>
      <TabsList className={cn('grid w-full grid-cols-2 h-fit')}>
        <TabsTrigger
          className={'whitespace-normal'}
          value={FORM_FIELDS.Select}
          onClick={() => setSelectedTab(FORM_FIELDS.Select)}
          disabled={templates?.length === 0}
          data-testid={`addtotemplate-tab-select`}
        >
          {t(isAgh ? 'Zur Einkaufsliste hinzufügen' : 'Zur Bestellvorlage hinzufügen')}
        </TabsTrigger>
        <TabsTrigger
          className={'whitespace-normal'}
          value={FORM_FIELDS.Create}
          onClick={() => setSelectedTab(FORM_FIELDS.Create)}
          data-testid={`addtotemplate-tab-create`}
        >
          {t(isAgh ? 'Neue Einkaufsliste erstellen' : 'Neue Bestellvorlage erstellen')}
        </TabsTrigger>
      </TabsList>
      <TabsContent value={FORM_FIELDS.Select} className={'desktop:min-h-[170px] flex-1'}>
        <TemplateSelect
          preselectedTemplateId={latestTemplateId}
          onClose={onClose}
          articles={articles}
          templates={templates ?? []}
          isLoading={isLoading || !templates}
          isAgh={isAgh}
        />
      </TabsContent>
      <TabsContent value={FORM_FIELDS.Create} className={'desktop:min-h-[170px] flex-1'}>
        <TemplateCreate onClose={onClose} articles={articles} isAgh={isAgh} />
      </TabsContent>
    </Tabs>
  );
}
